import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { clientActions, userActions, modalActions, toastActions } from 'actions';
import {
  selectClientCollectionByFilter,
  selectClientCollectionCountByFilter,
} from 'selectors/client';
import { selectCurrentUser } from 'selectors/user';
import { getPath, history } from 'helpers';
import { rolesConstants } from 'constants/Roles';
import { toastConstants } from 'constants/Toast.constants';
import Spinner from 'components/Core/Spinner/Spinner';
import { selectCurrentPlatformId } from 'selectors/platform';
import { clientService } from 'services';
import sidebarDefs from './sidebarDefinitions';
import columnDefs, { defaultColDef } from './columnDefinitions';
import localeText from './locales';

// --- localStorage helpers for persisting filter state ---
const FILTER_STORAGE_KEY = 'clientAggrid_filterModel';

function getSavedFilterModel(platformId) {
  try {
    const raw = localStorage.getItem(`${FILTER_STORAGE_KEY}_${platformId}`);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore parse errors
  }
  return null;
}

function saveFilterModel(platformId, filterModel) {
  try {
    // Only store if there are active filters, otherwise clean up
    if (filterModel && Object.keys(filterModel).length > 0) {
      localStorage.setItem(`${FILTER_STORAGE_KEY}_${platformId}`, JSON.stringify(filterModel));
    } else {
      localStorage.removeItem(`${FILTER_STORAGE_KEY}_${platformId}`);
    }
  } catch (e) {
    // ignore storage errors
  }
}

const ClientAggrid = ({
  fetchAllClients,
  clientList,
  updateUserInfo,
  updateClientEmail,
  currentPlatformId,
  currentUser,
  openBulkAddSelectionModal,
  pushToast,
}) => {
  const frameworkComponents = {
    Spinner,
  };
  const userRoles = (currentUser && currentUser.roles) || [];
  const canBulkCreateSelection =
    Array.isArray(userRoles) &&
    (userRoles.includes(rolesConstants.ROLE_ADMIN) ||
      userRoles.includes(rolesConstants.ROLE_SUPER_ADMIN));
  const isClientLocked = (client) => !!(client && Number(client.locked));

  // Strip out any filter with empty string so the backend doesn't get "match nothing"
  const normalizeFilterModel = (model) => {
    if (!model || typeof model !== 'object') return null;
    const out = {};
    Object.keys(model).forEach((colId) => {
      const m = model[colId];
      const filterValue = m && m.filter;
      const isEmpty =
        filterValue == null || (typeof filterValue === 'string' && filterValue.trim() === '');
      if (!isEmpty) out[colId] = m;
    });
    return Object.keys(out).length === 0 ? null : out;
  };

  const datasource = {
    getRows(params) {
      const { startRow, endRow, filterModel, sortModel } = params.request;
      const safeFilterModel = normalizeFilterModel(filterModel);
      gridRef.current.api.hideOverlay();
      gridRef.current.api.showLoadingOverlay();
      clientService
        .fetchClientsSSRM(currentPlatformId, safeFilterModel, startRow, 10)
        .then((response) => {
          if (response.rowcount === 0) gridRef.current.api.showNoRowsOverlay();
          if (response.rowcount > 0) gridRef.current.api.hideOverlay();
          params.successCallback(response.rowdata, response.rowcount);
        })
        .catch((error) => {
          params.failCallback();
        });
    },
  };

  const onGridReady = (params) => {
    const gridApi = params.api;

    gridApi.sizeColumnsToFit();
    window.onresize = () => {
      gridApi.sizeColumnsToFit();
    };

    // Restore saved filters from localStorage before setting datasource
    const savedFilter = getSavedFilterModel(currentPlatformId);
    const normalizedSaved = normalizeFilterModel(savedFilter);
    if (normalizedSaved) {
      gridApi.setFilterModel(normalizedSaved);
    }

    params.api.setServerSideDatasource(datasource);
  };

  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  const onToolPanelVisibleChanged = (params) => {
    const gridApi = params.api;
    gridApi.sizeColumnsToFit();
  };

  const onFilterChanged = (params) => {
    const gridApi = params.api;
    gridApi.hideOverlay();

    // Save current filter model to localStorage so it persists on navigation
    const filterModel = gridApi.getFilterModel();
    saveFilterModel(currentPlatformId, filterModel);

    // Clear "select all" state when filters change to prevent stale selections
    // (the stored client list no longer matches the new filter results)
    if (selectAllRef.current || allSelectedClients) {
      selectAllRef.current = false;
      setAllSelectedClients(null);
      setSelectedCount(0);
      gridApi.deselectAll();
    }
  };

  const [selectedCount, setSelectedCount] = useState(0);
  // When "Tout sélectionner" is used, we store ALL clients from the server here
  // because Ag-Grid SSRM only knows about loaded rows (1 page = 10 rows)
  const [allSelectedClients, setAllSelectedClients] = useState(null);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const selectAllButtonClassName = [
    'client-list__toolbar-button',
    selectedCount > 0 ? 'client-list__toolbar-button--active' : '',
    isLoadingAll ? 'client-list__toolbar-button--loading' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const bulkActionSelectClassName = [
    'client-list__bulk-action-select',
    selectedCount === 0 ? 'client-list__bulk-action-select--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Ref to track "select all" mode inside Ag-Grid callbacks
  // (callbacks are closures that would see stale state otherwise)
  const selectAllRef = useRef(false);

  const onSelectionChanged = (event) => {
    // If we're in "select all" mode, don't override the count from manual checkbox events
    if (selectAllRef.current) return;
    if (event && event.api) {
      const nodes = event.api.getSelectedNodes() || [];
      const count = nodes.filter((n) => n.data).length;
      setSelectedCount(count);
      // If user manually deselects all, clear the "select all" override
      if (allSelectedClients && count === 0) {
        setAllSelectedClients(null);
      }
    }
  };

  // In "select all" mode, prevent per-row deselection to keep state consistent.
  const onRowSelected = (event) => {
    if (!selectAllRef.current || !event || !event.node) return;
    if (event.node.data && !isClientLocked(event.node.data) && !event.node.isSelected()) {
      event.node.setSelected(true);
    }
  };

  const selectVisibleRowsWhenSelectAllEnabled = (api) => {
    if (!selectAllRef.current || !api) return;
    api.forEachNode((node) => {
      if (node.data && !isClientLocked(node.data) && !node.isSelected()) {
        node.setSelected(true);
      }
    });
  };

  // Re-apply row selection when grid model/store updates (pagination, SSRM refresh, filtering, etc.).
  const onModelUpdated = (event) => {
    if (!event || !event.api) return;
    selectVisibleRowsWhenSelectAllEnabled(event.api);
  };

  const onStoreRefreshed = (event) => {
    if (!event || !event.api) return;
    selectVisibleRowsWhenSelectAllEnabled(event.api);
  };

  const onCellClicked = (event) => {
    if (
      event.column &&
      ['clientMail', 'clientAction', 'clientSelect'].includes(event.column.colId)
    ) {
      return;
    }
    if (getPath('clientDetail') && event && event.node && event.node.data && event.node.data.id) {
      history.push(getPath('clientDetail').replace(':id', event.node.data.id));
    }
  };

  // Get the list of selected clients: from "select all" state or from grid checkboxes
  const getSelectedClients = () => {
    // If "Tout sélectionner" was used, we have all clients from the server
    if (allSelectedClients && allSelectedClients.length > 0) {
      return allSelectedClients;
    }
    // Otherwise, use manually checked rows from the grid
    const { current } = gridRef;
    if (!current || !current.api) return [];
    const { api } = current;
    const selectedNodes = api.getSelectedNodes();
    return (selectedNodes || [])
      .filter((node) => node.data && node.data.id && !isClientLocked(node.data))
      .map((node) => ({ id: node.data.id, ext_code: node.data.ext_code, name: node.data.name }));
  };

  const handleBulkActionChange = (e) => {
    const { value } = e.target;
    if (!value || value === '') return;
    e.target.value = '';
    if (value === 'bulkAddSelection') {
      if (!canBulkCreateSelection) return;
      const selectedClients = getSelectedClients();
      if (selectedClients.length > 0) {
        openBulkAddSelectionModal(selectedClients);
      }
    }
    // Add more bulk action handlers here, e.g.:
    // if (value === 'bulkExport') { ... }
  };

  const handleSelectAll = () => {
    const { current } = gridRef;
    if (!current || !current.api) return;
    const { api } = current;

    if (selectedCount > 0 || allSelectedClients) {
      // Deselect: clear grid checkboxes + clear our stored list
      selectAllRef.current = false;
      api.deselectAll();
      setAllSelectedClients(null);
      setSelectedCount(0);
    } else {
      // Select all: fetch ALL matching clients from the server
      setIsLoadingAll(true);
      const filterModel = api.getFilterModel() || {};
      const safeFilterModel = normalizeFilterModel(filterModel);
      const SELECT_ALL_LIMIT = 10000;
      clientService
        .fetchClientsSSRM(currentPlatformId, safeFilterModel, 0, SELECT_ALL_LIMIT)
        .then((response) => {
          const totalOnServer = response.rowcount || 0;
          const clients = (response.rowdata || [])
            .filter((row) => row && row.id && !isClientLocked(row))
            .map((row) => ({ id: row.id, ext_code: row.ext_code, name: row.name }));

          // Warn if total exceeds the limit we fetched
          if (totalOnServer > SELECT_ALL_LIMIT) {
            pushToast({
              type: toastConstants.TYPE_WARNING,
              message:
                `Attention : il y a ${totalOnServer} clients au total, ` +
                `mais seuls les ${SELECT_ALL_LIMIT} premiers ont été sélectionnés. ` +
                'Utilisez les filtres pour réduire la liste.',
            });
          }

          // Activate "select all" mode
          selectAllRef.current = true;
          setAllSelectedClients(clients);
          setSelectedCount(clients.length);
          // Check all currently visible rows in the grid
          selectVisibleRowsWhenSelectAllEnabled(api);
          setIsLoadingAll(false);
        })
        .catch((error) => {
          const message =
            (error && error.message) ||
            (typeof error === 'string' ? error : 'Impossible de sélectionner les clients.');
          pushToast({
            type: toastConstants.TYPE_ERROR,
            message,
          });
          setIsLoadingAll(false);
        });
    }
  };

  const onCellValueChanged = (params) => {
    if (params.column.colId === 'clientMail') {
      updateUserInfo(
        { id: params.data.customers[0].id, email: params.newValue },
        {
          success: "L'email à bien été mis à jour",
          error: "Une erreur s'est produite, veuillez réessayer",
        }
      );
      updateClientEmail();
    }
  };

  const pagination = true;
  const paginationPageSize = 10;

  const gridRef = useRef(null);

  return (
    <div className="AggridContainer ag-theme-balham">
      <div className="client-list__toolbar">
        {canBulkCreateSelection ? (
          <button
            className={selectAllButtonClassName}
            type="button"
            onClick={handleSelectAll}
            disabled={isLoadingAll}
          >
            {isLoadingAll
              ? 'Chargement...'
              : selectedCount > 0
              ? 'Tout désélectionner'
              : 'Tout sélectionner'}
          </button>
        ) : null}
        {canBulkCreateSelection ? (
          <span className="client-list__bulk-select">
            <select
              className={bulkActionSelectClassName}
              value=""
              onChange={handleBulkActionChange}
              disabled={selectedCount === 0}
              title={
                selectedCount === 0
                  ? 'Sélectionnez des clients'
                  : `Actions pour ${selectedCount} client(s)`
              }
            >
              <option value="">Actions en masse</option>
              <option value="bulkAddSelection">Créer une sélection</option>
            </select>
          </span>
        ) : null}
        {selectedCount > 0 && (
          <span className="client-list__selected-count">
            {selectedCount} client(s) sélectionné(s)
            {allSelectedClients ? ' (tous)' : ''}
          </span>
        )}
      </div>
      <AgGridReact
        ref={gridRef}
        // sideBar={sidebarDefs}
        columnDefs={columnDefs}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        cacheBlockSize={10}
        domLayout="autoHeight"
        rowModelType="serverSide"
        serverSideStoreType="serverSideStoreType"
        overlayNoRowsTemplate={
          '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow">No clients found</span>'
        }
        rowSelection="multiple"
        onGridReady={onGridReady}
        suppressRowClickSelection
        onSelectionChanged={onSelectionChanged}
        onRowSelected={onRowSelected}
        onFirstDataRendered={onFirstDataRendered}
        onToolPanelVisibleChanged={onToolPanelVisibleChanged}
        onFilterChanged={onFilterChanged}
        onModelUpdated={onModelUpdated}
        onStoreRefreshed={onStoreRefreshed}
        onCellClicked={onCellClicked}
        localeText={localeText}
        defaultColDef={defaultColDef}
        frameworkComponents={frameworkComponents}
        pivotMode={false}
        tooltipShowDelay={0}
        rowHeight={45}
        onCellValueChanged={onCellValueChanged}
        loadingOverlayComponent="Spinner"
        // groupDefaultExpanded={-1}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const currentPlatformId = selectCurrentPlatformId(state);
  return {
    clientList: selectClientCollectionByFilter(state, { platform: currentPlatformId }),
    currentPlatformId,
    currentUser: selectCurrentUser(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchAllClients: (filter) => dispatch(clientActions.fetchClients(filter, 0, 10000)),
  updateUserInfo: (user, withToast) => dispatch(userActions.updateInfo(user, withToast)),
  updateClientEmail: (email) => dispatch(clientActions.updateClientEmail(email)),
  pushToast: (toast) => dispatch(toastActions.addToast(toast)),
  openBulkAddSelectionModal: (selectedClients) =>
    dispatch(
      modalActions.open('bulkAddSelection', {
        modalHeader: 'Créer une sélection pour plusieurs clients',
        selectedClients,
        width: '460',
      })
    ),
});

ClientAggrid.propTypes = {
  fetchAllClients: PropTypes.func,
  clientList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  updateUserInfo: PropTypes.func,
  updateClientEmail: PropTypes.func,
  openBulkAddSelectionModal: PropTypes.func,
  pushToast: PropTypes.func,
  currentUser: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientAggrid);
